import React, { useEffect, useMemo, useState } from "react";
import {
  Building2,
  HeartHandshake,
  Sparkles,
  Phone,
  Mail,
  ExternalLink,
  Share2,
  CalendarCheck,
  MessageSquare,
  ArrowRight,
  SlidersHorizontal,
  BadgeCheck,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { ccCard, ccInput, ccBtnPink, ccBtnBlue, ccBtnGhost, Avatar, EmptyState, CCModal, ModalHeader } from "./ui";
import { SERVICE_TYPES } from "./careData";
import { FindCare } from "./Browse";

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE = 10;

const TABS = [
  { key: "daycares", label: "Daycares", icon: Building2 },
  { key: "caregivers", label: "Caregivers", icon: HeartHandshake },
  { key: "services", label: "Other Services", icon: Sparkles },
];

const az = (list) => [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

const trackFeatured = (id, type) =>
  fetch(`${API}/api/featured-daycares/${id}/track`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type }),
  }).catch(() => {});

const trackProvider = (id, type) =>
  fetch(`${API}/api/providers/${id}/track`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type }),
  }).catch(() => {});

// ---------- Public caregiver profile modal (bio, type, contact, booking) ----------
const CaregiverModal = ({ item, ctx, onClose }) => {
  const [full, setFull] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/public/caregiver/${item.id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setFull)
      .catch(() => {});
  }, [item.id]);

  const p = full || {};
  const name = p.name || item.name;
  const type = (p.caregiver_type || item.caregiver_type || "Caregiver").trim();
  const headline = (p.headline || "").trim();
  const phone = p.phone || item.phone || "";
  const email = p.email || item.email || "";
  const since = p.member_since || item.member_since;
  const photo = p.photo || item.photo;
  const signedIn = Boolean(ctx.currentUser?.real);
  const needSignIn = (action) => toast.info(`Sign in to ${action} — it keeps families and caregivers safe.`);

  return (
    <CCModal onClose={onClose} testId="dir-caregiver-modal">
      <ModalHeader title={name} icon={HeartHandshake} onClose={onClose} closeTestId="dir-caregiver-close" />
      <div className="flex items-center gap-4 mb-4">
        <Avatar src={photo} name={name} size="w-20 h-20" />
        <div className="min-w-0">
          <span className="inline-block px-2.5 py-1 bg-[#E6F2FA] border border-[#B3E0F2] text-[#3D7E9E] text-[10px] font-extrabold rounded-full uppercase tracking-wide" data-testid="dir-caregiver-type">
            {type}
          </span>
          <p className="flex items-center gap-1.5 mt-2 text-[11px] font-bold text-[#2F6031]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7CC98F]" /> Open to hire
          </p>
          {since && (
            <p className="text-[11px] text-[#9CA3AF] font-medium mt-1">
              On NestTrack since {new Date(since).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
            </p>
          )}
        </div>
      </div>

      <h4 className="text-xs font-extrabold font-heading text-[#374151] uppercase tracking-wider mb-1.5 dark:text-slate-200">About</h4>
      <p className="text-sm text-[#6B7280] leading-relaxed font-medium dark:text-slate-300" data-testid="dir-caregiver-bio">
        {headline || `${name} is a ${type.toLowerCase()} on NestTrack Care Connections — verified care with transparent reviews and direct family messaging.`}
      </p>

      <h4 className="text-xs font-extrabold font-heading text-[#374151] uppercase tracking-wider mt-5 mb-2 dark:text-slate-200">Services</h4>
      <div className="flex gap-1.5 flex-wrap" data-testid="dir-caregiver-services">
        <span className="px-2.5 py-1 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] text-[11px] font-bold rounded-full flex items-center gap-1">
          <BadgeCheck className="w-3 h-3" /> {type}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-6">
        <button
          onClick={() => (signedIn ? ctx.openRequestModal(item.id) : needSignIn("request care"))}
          data-testid="dir-caregiver-request"
          className={ccBtnPink}
        >
          <CalendarCheck className="w-4 h-4 inline mr-1.5 -mt-0.5" /> Request Care
        </button>
        <button
          onClick={() => (signedIn ? ctx.openMessages(item.id) : needSignIn("message caregivers"))}
          data-testid="dir-caregiver-message"
          className={ccBtnBlue}
        >
          <MessageSquare className="w-4 h-4 inline mr-1.5 -mt-0.5" /> Message
        </button>
        {phone && (
          <a href={`tel:${String(phone).replace(/[^\d+]/g, "")}`} data-testid="dir-caregiver-call" className={`${ccBtnGhost} flex items-center justify-center`}>
            <Phone className="w-4 h-4 inline mr-1.5 -mt-0.5 text-[#2F6031]" /> Call
          </a>
        )}
        {email && (
          <a href={`mailto:${email}?subject=Care%20inquiry%20via%20NestTrack`} data-testid="dir-caregiver-email" className={`${ccBtnGhost} flex items-center justify-center`}>
            <Mail className="w-4 h-4 inline mr-1.5 -mt-0.5 text-[#3D7E9E]" /> Email
          </a>
        )}
        <button
          onClick={() => {
            navigator.clipboard.writeText(`${API}/api/share/profile/${item.id}`);
            toast.success("Share link copied — it renders a rich preview card when posted anywhere.");
          }}
          data-testid="dir-caregiver-share"
          className={`${ccBtnGhost} flex items-center justify-center`}
        >
          <Share2 className="w-4 h-4 inline mr-1.5 -mt-0.5" /> Share Profile
        </button>
      </div>
    </CCModal>
  );
};

// ---------- Daycare / facility detail modal ----------
const DaycareModal = ({ item, onClose }) => {
  useEffect(() => {
    if (item.shareType === "featured" && item.id) trackFeatured(item.id, "view");
  }, [item]);
  const shareUrl = `${API}/api/share/${item.shareType === "featured" ? "featured" : "facility"}/${item.id}`;
  return (
    <CCModal onClose={onClose} testId="dir-daycare-modal">
      <ModalHeader title={item.name} icon={Building2} onClose={onClose} closeTestId="dir-daycare-close" />
      {item.photo && (
        <img
          src={item.photo}
          alt={item.name}
          className="w-full aspect-video object-cover rounded-2xl mb-4 border border-[#FCE6E9]"
          style={item.focal ? { objectPosition: `${item.focal.x}% ${item.focal.y}%` } : undefined}
        />
      )}
      <span className={`inline-block px-2.5 py-1 text-[10px] font-extrabold rounded-full uppercase tracking-wide mb-3 ${
        item.kind === "Featured Daycare" ? "bg-[#FFF2D1] border border-[#F5E3B3] text-[#A08244]" : "bg-[#E6F2FA] border border-[#B3E0F2] text-[#3D7E9E]"
      }`}>
        {item.kind}
      </span>
      <p className="text-sm text-[#6B7280] font-medium leading-relaxed dark:text-slate-300" data-testid="dir-daycare-description">
        {item.description || item.detail}
      </p>
      {item.video && <video src={item.video} controls playsInline className="w-full rounded-2xl mt-4 max-h-56 bg-black" data-testid="dir-daycare-video" />}
      <div className="space-y-2.5 mt-5">
        {(item.phone || item.email) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {item.phone && (
              <a href={`tel:${String(item.phone).replace(/[^\d+]/g, "")}`} data-testid="dir-daycare-call" className={`${ccBtnGhost} flex items-center justify-center`}>
                <Phone className="w-4 h-4 inline mr-1.5 -mt-0.5 text-[#2F6031]" /> {item.phone}
              </a>
            )}
            {item.email && (
              <a href={`mailto:${item.email}?subject=Enrollment%20inquiry%20via%20NestTrack`} data-testid="dir-daycare-email" className={`${ccBtnGhost} flex items-center justify-center`}>
                <Mail className="w-4 h-4 inline mr-1.5 -mt-0.5 text-[#3D7E9E]" /> Email
              </a>
            )}
          </div>
        )}
        {item.enroll_url && (
          <a
            href={item.enroll_url}
            target="_blank"
            rel="noreferrer"
            onClick={() => item.shareType === "featured" && trackFeatured(item.id, "click")}
            data-testid="dir-daycare-enroll"
            className={`${ccBtnPink} w-full flex items-center justify-center`}
          >
            <ExternalLink className="w-4 h-4 inline mr-1.5 -mt-0.5" /> Enroll / Visit Website
          </a>
        )}
        <button
          onClick={() => {
            navigator.clipboard.writeText(shareUrl);
            toast.success("Share link copied — it renders a rich preview card when posted anywhere.");
          }}
          data-testid="dir-daycare-share"
          className={`${ccBtnGhost} w-full flex items-center justify-center`}
        >
          <Share2 className="w-4 h-4 inline mr-1.5 -mt-0.5" /> Share this daycare
        </button>
      </div>
    </CCModal>
  );
};

// ---------- Cards ----------
const CaregiverDirCard = ({ c, onOpen, onMessage }) => (
  <button
    onClick={() => onOpen(c)}
    data-testid={`dir-caregiver-card-${c.id}`}
    className={`${ccCard} p-5 w-full text-left hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer`}
  >
    <div className="flex items-center gap-4">
      <Avatar src={c.photo} name={c.name} size="w-14 h-14" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-extrabold text-[#374151] font-heading truncate dark:text-slate-100">{c.name}</p>
        <p className="text-[11px] text-[#6B7280] truncate font-medium dark:text-slate-400">{c.detail}</p>
        <div className="flex gap-1.5 mt-2 flex-wrap">
          {c.caregiver_type && (
            <span className="px-2 py-0.5 bg-[#E6F2FA] border border-[#B3E0F2] text-[#3D7E9E] text-[9px] font-extrabold rounded-full uppercase">
              {c.caregiver_type}
            </span>
          )}
          <span className="px-2 py-0.5 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] text-[9px] font-extrabold rounded-full uppercase">
            Open to hire
          </span>
          {c.verification_status === "verified" ? (
            <span data-testid={`dir-caregiver-verified-${c.id}`} className="px-2 py-0.5 bg-[#E2F4E1] border border-[#7CC98F] text-[#2F6031] text-[9px] font-extrabold rounded-full uppercase inline-flex items-center gap-0.5">
              <ShieldCheck className="w-2.5 h-2.5" /> Verified
            </span>
          ) : (
            <span data-testid={`dir-caregiver-unverified-${c.id}`} className="px-2 py-0.5 bg-[#F5E3B3]/30 border border-[#F5E3B3] text-[#8a6d1a] text-[9px] font-extrabold rounded-full uppercase">
              Unverified
            </span>
          )}
        </div>
      </div>
    </div>
    <div className="mt-3 flex items-center justify-between gap-2">
      <p className="text-[11px] font-extrabold text-[#5CB3DC] flex items-center gap-1">
        View full profile <ArrowRight className="w-3 h-3" />
      </p>
      <span className="flex items-center gap-1">
        {c.email && (
          <a
            href={`mailto:${c.email}?subject=Care%20inquiry%20via%20NestTrack`}
            onClick={(e) => e.stopPropagation()}
            title={`Email ${c.name}`}
            data-testid={`dir-caregiver-quickemail-${c.id}`}
            className="w-7 h-7 rounded-full bg-[#E6F2FA] border border-[#B3E0F2] flex items-center justify-center text-[#3D7E9E] hover:bg-[#B3E0F2] transition-colors"
          >
            <Mail className="w-3.5 h-3.5" />
          </a>
        )}
        {c.phone && (
          <a
            href={`tel:${String(c.phone).replace(/[^\d+]/g, "")}`}
            onClick={(e) => e.stopPropagation()}
            title={`Call ${c.name}`}
            data-testid={`dir-caregiver-quickcall-${c.id}`}
            className="w-7 h-7 rounded-full bg-[#E2F4E1] border border-[#C4E5C2] flex items-center justify-center text-[#2F6031] hover:bg-[#C4E5C2] transition-colors"
          >
            <Phone className="w-3.5 h-3.5" />
          </a>
        )}
        <span
          role="button"
          tabIndex={0}
          title="Message"
          onClick={(e) => {
            e.stopPropagation();
            onMessage(c);
          }}
          data-testid={`dir-caregiver-quickmsg-${c.id}`}
          className="w-7 h-7 rounded-full bg-[#FCE6E9] border border-[#F8C8DC] flex items-center justify-center text-[#B2607F] hover:bg-[#F8C8DC] transition-colors cursor-pointer"
        >
          <MessageSquare className="w-3.5 h-3.5" />
        </span>
      </span>
    </div>
  </button>
);

const DaycareDirCard = ({ d, onOpen }) => (
  <button
    onClick={() => onOpen(d)}
    data-testid={`dir-daycare-card-${d.id}`}
    className={`${ccCard} p-5 w-full text-left hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer`}
  >
    <div className="flex items-center gap-4">
      {d.photo ? (
        <img
          src={d.photo}
          alt={d.name}
          className="w-14 h-14 rounded-2xl object-cover border-2 border-[#FCE6E9] shrink-0"
          style={d.focal ? { objectPosition: `${d.focal.x}% ${d.focal.y}%` } : undefined}
        />
      ) : (
        <span className="w-14 h-14 rounded-2xl bg-[#E6F2FA] flex items-center justify-center shrink-0">
          <Building2 className="w-6 h-6 text-[#5CB3DC]" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-extrabold text-[#374151] font-heading truncate dark:text-slate-100">{d.name}</p>
        <p className="text-[11px] text-[#6B7280] truncate font-medium dark:text-slate-400">{d.detail}</p>
        {d.kind === "Featured Daycare" && (
          <span className="inline-block mt-2 px-2 py-0.5 bg-[#FFF2D1] border border-[#F5E3B3] text-[#A08244] text-[9px] font-extrabold rounded-full uppercase">
            Featured
          </span>
        )}
      </div>
    </div>
    <p className="mt-3 text-[11px] font-extrabold text-[#5CB3DC] flex items-center gap-1">
      View details <ArrowRight className="w-3 h-3" />
    </p>
  </button>
);

const ProviderCard = ({ p }) => (
  <div className={`${ccCard} p-5`} data-testid={`dir-provider-card-${p.id || p.name}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-extrabold text-[#374151] font-heading dark:text-slate-100">{p.name}</p>
        <div className="flex gap-1.5 mt-1.5 flex-wrap">
          {p.type && (
            <span className="px-2 py-0.5 bg-[#FCE6E9] border border-[#F8C8DC] text-[#B2607F] text-[9px] font-extrabold rounded-full uppercase">{p.type}</span>
          )}
          {p.state && (
            <span className="px-2 py-0.5 bg-[#E6F2FA] border border-[#B3E0F2] text-[#3D7E9E] text-[9px] font-extrabold rounded-full uppercase">{p.state}</span>
          )}
        </div>
      </div>
    </div>
    {p.description && <p className="text-[11px] text-[#6B7280] font-medium leading-relaxed mt-2.5 line-clamp-2 dark:text-slate-400">{p.description}</p>}
    <div className="flex gap-2 mt-3.5 flex-wrap">
      {p.website && (
        <a
          href={p.website}
          target="_blank"
          rel="noreferrer"
          onClick={() => p.id && trackProvider(p.id, "click")}
          data-testid={`dir-provider-visit-${p.id || p.name}`}
          className="px-3.5 py-1.5 bg-[#E6F2FA] hover:bg-[#B3E0F2] text-[#3D7E9E] rounded-full text-[11px] font-bold transition-colors flex items-center gap-1"
        >
          <ExternalLink className="w-3 h-3" /> Visit Website
        </a>
      )}
      {p.phone && (
        <a
          href={`tel:${String(p.phone).replace(/[^\d+]/g, "")}`}
          onClick={() => p.id && trackProvider(p.id, "call")}
          data-testid={`dir-provider-call-${p.id || p.name}`}
          className="px-3.5 py-1.5 bg-[#E2F4E1] hover:bg-[#C4E5C2] text-[#2F6031] rounded-full text-[11px] font-bold transition-colors flex items-center gap-1"
        >
          <Phone className="w-3 h-3" /> Call
        </a>
      )}
    </div>
  </div>
);

// ---------- Tabbed public directory (Find Care screen) ----------
export default function CareDirectory({ ctx, initialTab }) {
  const [tab, setTab] = useState(TABS.some((t) => t.key === initialTab) ? initialTab : "caregivers");
  const [daycares, setDaycares] = useState(null);
  const [caregivers, setCaregivers] = useState(null);
  const [providers, setProviders] = useState(null);
  const [providerTypes, setProviderTypes] = useState([]);
  const [shown, setShown] = useState({ daycares: PAGE, caregivers: PAGE, services: PAGE });
  const [typeFilter, setTypeFilter] = useState("All");
  const [svcQuery, setSvcQuery] = useState("");
  const [svcType, setSvcType] = useState("");
  const [cgModal, setCgModal] = useState(null);
  const [dcModal, setDcModal] = useState(null);
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/public/showcase`)
      .then((r) => (r.ok ? r.json() : { caregivers: [], daycares: [] }))
      .then((d) => {
        setCaregivers(az(d.caregivers || []));
        // Daycares with photos surface first, then plain A-Z within each group (stable sort keeps A-Z)
        setDaycares(az(d.daycares || []).sort((a, b) => (b.photo ? 1 : 0) - (a.photo ? 1 : 0)));
      })
      .catch(() => {
        setCaregivers([]);
        setDaycares([]);
      });
    fetch(`${API}/api/providers`)
      .then((r) => (r.ok ? r.json() : { providers: [] }))
      .then((d) => setProviders(az(d.providers || [])))
      .catch(() => setProviders([]));
    fetch(`${API}/api/providers/meta`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => setProviderTypes(d.types || []))
      .catch(() => {});
  }, []);

  const caregiverTypes = useMemo(
    () => ["All", ...new Set((caregivers || []).map((c) => c.caregiver_type).filter(Boolean))],
    [caregivers]
  );
  const visibleCaregivers = useMemo(
    () => (caregivers || []).filter((c) => typeFilter === "All" || c.caregiver_type === typeFilter),
    [caregivers, typeFilter]
  );
  const visibleProviders = useMemo(() => {
    const q = svcQuery.trim().toLowerCase();
    return (providers || []).filter(
      (p) =>
        (!svcType || p.type === svcType) &&
        (!q || [p.name, p.description, p.type, p.state].some((v) => (v || "").toLowerCase().includes(q)))
    );
  }, [providers, svcQuery, svcType]);

  const more = (key) => setShown((s) => ({ ...s, [key]: s[key] + PAGE }));

  const CountLine = ({ total, shownCount }) => (
    <p className="text-center text-[11px] font-bold text-[#9CA3AF] mt-4" data-testid={`dir-count-${tab}`}>
      Showing {Math.min(shownCount, total)} of {total}
    </p>
  );

  const LoadMore = ({ list, tabKey }) =>
    list.length > shown[tabKey] ? (
      <div className="text-center mt-4">
        <button onClick={() => more(tabKey)} data-testid={`dir-load-more-${tabKey}`} className={ccBtnGhost}>
          Load Next {Math.min(PAGE, list.length - shown[tabKey])}
        </button>
      </div>
    ) : null;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="care-directory">
      <div className="text-center">
        <h2 className="text-2xl font-black font-heading text-[#374151] dark:text-slate-100 tracking-tight" data-testid="dir-title">
          Care Directory
        </h2>
        <p className="text-xs text-[#9CA3AF] font-medium mt-1">Every daycare, caregiver, and care program on NestTrack — sorted A to Z.</p>
      </div>

      <div className="flex gap-1.5 p-1.5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-full w-fit mx-auto flex-wrap justify-center dark:bg-[#273244] dark:border-[#374457]" data-testid="dir-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`dir-tab-${t.key}`}
            className={`flex items-center gap-1.5 px-5 py-2.5 rounded-full text-xs font-extrabold transition-all duration-200 ${
              tab === t.key ? "bg-[#F8C8DC] text-[#374151] shadow-sm" : "text-[#9CA3AF] hover:text-[#374151]"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "daycares" && (
        <div data-testid="dir-panel-daycares">
          {daycares === null ? (
            <p className="text-center text-xs font-bold text-[#9CA3AF] py-10">Loading daycares…</p>
          ) : daycares.length === 0 ? (
            <EmptyState text="No daycares listed yet — check back soon." testId="dir-daycares-empty" />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="dir-grid-daycares">
                {daycares.slice(0, shown.daycares).map((d) => (
                  <DaycareDirCard key={d.id} d={d} onOpen={setDcModal} />
                ))}
              </div>
              <CountLine total={daycares.length} shownCount={shown.daycares} />
              <LoadMore list={daycares} tabKey="daycares" />
            </>
          )}
        </div>
      )}

      {tab === "caregivers" && (
        <div data-testid="dir-panel-caregivers">
          {caregiverTypes.length > 2 && (
            <div className="flex gap-2 flex-wrap justify-center mb-5" data-testid="dir-type-filters">
              {caregiverTypes.map((t) => (
                <button
                  key={t}
                  onClick={() => setTypeFilter(t)}
                  data-testid={`dir-type-chip-${t.replace(/\W/g, "-").toLowerCase()}`}
                  className={`px-3.5 py-1.5 rounded-full text-[11px] font-bold border transition-all ${
                    typeFilter === t
                      ? "bg-[#F8C8DC] border-[#EAAFC5] text-[#374151] shadow-sm"
                      : "bg-white border-[#FCE6E9] text-[#6B7280] hover:border-[#F8C8DC] dark:bg-[#273244] dark:border-[#374457]"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          )}
          {caregivers === null ? (
            <p className="text-center text-xs font-bold text-[#9CA3AF] py-10">Loading caregivers…</p>
          ) : visibleCaregivers.length === 0 ? (
            <EmptyState text="No caregivers listed yet — be the first to join from My Caregiving." testId="dir-caregivers-empty" />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="dir-grid-caregivers">
                {visibleCaregivers.slice(0, shown.caregivers).map((c) => (
                  <CaregiverDirCard
                    key={c.id}
                    c={c}
                    onOpen={setCgModal}
                    onMessage={(cg) => (ctx.currentUser?.real ? ctx.openMessages(cg.id) : toast.info("Sign in to message caregivers — it keeps families and caregivers safe."))}
                  />
                ))}
              </div>
              <CountLine total={visibleCaregivers.length} shownCount={shown.caregivers} />
              <LoadMore list={visibleCaregivers} tabKey="caregivers" />
            </>
          )}
          <div className="text-center mt-6">
            <button onClick={() => setShowSearch((s) => !s)} data-testid="dir-advanced-search-toggle" className={ccBtnGhost}>
              <SlidersHorizontal className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              {showSearch ? "Hide detailed search" : "Refine with a detailed search"}
            </button>
          </div>
          {showSearch && <div className="mt-5"><FindCare ctx={ctx} /></div>}
        </div>
      )}

      {tab === "services" && (
        <div className="space-y-5" data-testid="dir-panel-services">
          <div className={`${ccCard} p-5`}>
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-[#9CA3AF] mb-3">Browse care categories</p>
            <div className="flex gap-2 flex-wrap" data-testid="dir-category-chips">
              {SERVICE_TYPES.map((s) => (
                <button
                  key={s}
                  onClick={() => setTab("caregivers")}
                  data-testid={`dir-category-${s.replace(/\W/g, "-").toLowerCase()}`}
                  className="px-3.5 py-2 rounded-full text-xs font-bold bg-white border border-[#FCE6E9] text-[#6B7280] hover:border-[#F8C8DC] hover:text-[#374151] transition-all dark:bg-[#273244] dark:border-[#374457]"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="text"
              value={svcQuery}
              onChange={(e) => setSvcQuery(e.target.value)}
              placeholder="Search programs by name, type, or state…"
              className={ccInput}
              data-testid="dir-services-search"
            />
            <select value={svcType} onChange={(e) => setSvcType(e.target.value)} className={ccInput} data-testid="dir-services-type">
              <option value="">All program types</option>
              {providerTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          {providers === null ? (
            <p className="text-center text-xs font-bold text-[#9CA3AF] py-10">Loading programs…</p>
          ) : visibleProviders.length === 0 ? (
            <EmptyState text="No programs match that search." testId="dir-services-empty" />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="dir-grid-services">
                {visibleProviders.slice(0, shown.services).map((p) => (
                  <ProviderCard key={p.id || p.name} p={p} />
                ))}
              </div>
              <CountLine total={visibleProviders.length} shownCount={shown.services} />
              <LoadMore list={visibleProviders} tabKey="services" />
            </>
          )}
        </div>
      )}

      {cgModal && <CaregiverModal item={cgModal} ctx={ctx} onClose={() => setCgModal(null)} />}
      {dcModal && <DaycareModal item={dcModal} onClose={() => setDcModal(null)} />}
    </div>
  );
}
